import { z } from 'zod';
import { DOCUMENT_PAPER_KINDS } from './branding.js';

/**
 * تصديرُ جدولٍ معروضٍ ملفًّا — إكسل أو PDF.
 *
 * شكلٌ واحدٌ للمستند تبنيه أيُّ صفحة: القوائمُ المالية، وقوائمُ التحصيل
 * والفواتير والمصروفات والقيود. الواجهةُ تبنيه من الصفوف التي عرضتها،
 * والخادمُ يحوّله إكسل (exceljs هناك)، وصفحةُ الطباعة تحوّله PDF بيد
 * المتصفّح — فالعربيّةُ موصولةٌ في الاثنين.
 *
 * المستند أوراقٌ (ورقةٌ في إكسل، وصفحةٌ في PDF)، والورقةُ أقسامٌ (جدولٌ
 * لكلّ قسم بأعمدته وإجماليّه)، فتكفي قائمةً بسيطة بقسمٍ واحد وقائمةً مالية
 * بأقسامها.
 */

/**
 * نوعُ العمود: يحدّد تنسيقَ الخلية. `money` رقمٌ برقمين عشريّين، و`percent` نسبةٌ مئويّة (١٢ = ١٢٪).
 * و`code` رقمُ مستندٍ أو قيدٍ («INV-2026-0120»): نصٌّ لاتينيٌّ لا يُكسر ولا تقلبه العربيّةُ حوله.
 */
export const TABLE_EXPORT_KINDS = ['text', 'money', 'number', 'percent', 'date', 'code'] as const;
export type TableExportKind = (typeof TABLE_EXPORT_KINDS)[number];

/** طرازُ الصف: `group` حسابٌ تجميعيّ بارز، `subtotal`/`total` إجماليّان، `heading` عنوانٌ فرعيّ، `note` ملاحظة. */
export const TABLE_EXPORT_ROW_STYLES = ['normal', 'group', 'heading', 'subtotal', 'total', 'note'] as const;
export type TableExportRowStyle = (typeof TABLE_EXPORT_ROW_STYLES)[number];

const Cell = z.union([z.string().max(2000), z.number().finite(), z.null()]).optional();

export const TableExportColumnSchema = z.object({
  key: z.string().trim().min(1).max(60),
  label: z.string().trim().max(120),
  kind: z.enum(TABLE_EXPORT_KINDS).default('text'),
  /** يُجمع العمود في صفّ الإجمالي تلقائيًّا (ما لم تُعطَ `totals` صريحة). */
  total: z.boolean().default(false),
  /** العمودُ الذي تُزاح فيه الصفوف بمستواها (`_level`)؛ وإلّا فأوّلُ عمودٍ نصّيّ. */
  indent: z.boolean().optional(),
  /** عرضُ العمود في إكسل بالحروف — يغلب العرضَ المحسوب من المحتوى. */
  width: z.number().int().min(4).max(120).optional(),
  /** أدنى عرضٍ بالحروف — في إكسل وPDF معًا — لعمودٍ نصّيٍّ يضيق فيكثر التفافه. */
  minWidth: z.number().int().min(2).max(120).optional(),
  /**
   * يلتفّ النصُّ أسطرًا أم يبقى سطرًا واحدًا. الافتراضيّ: النصُّ يلتفّ، وما
   * سواه (التاريخ والأرقام والمبالغ والرموز) سطرٌ واحدٌ لا يُكسر أبدًا.
   *
   * وفي خليّة النصّ يفصل `\n` سطرًا رئيسًا عن تفاصيل تحته: تُعرض في PDF
   * أصغرَ وأخفت، فيبقى البيانُ مقروءًا ولا تضيع التفاصيل.
   */
  wrap: z.boolean().optional(),
  /**
   * مفتاحُ رابط الخليّة في الصفّ: الصفُّ يحمل تحته عنوانًا مطلقًا (http/https)
   * فتصير الخليّةُ رابطًا — في PDF يُنقر فيفتح (فاتورةُ العميل كاملةً من كشف
   * حسابه)، وفي إكسل رابطٌ في الخليّة. المفتاحُ ليس عمودًا فلا يُعرض وحده.
   */
  hrefKey: z.string().trim().min(1).max(60).optional(),
});

export const TableExportRowSchema = z
  .record(z.string(), Cell)
  .and(
    z.object({
      /** إزاحةُ الصف في الشجرة (حسابٌ تحت أبيه) — تُطبَّق على أوّل عمودٍ نصّيّ. */
      _level: z.number().int().min(0).max(8).optional(),
      _style: z.enum(TABLE_EXPORT_ROW_STYLES).optional(),
    }),
  );

export const TableExportSectionSchema = z.object({
  title: z.string().trim().max(160).optional(),
  columns: z.array(TableExportColumnSchema).min(1).max(30),
  rows: z.array(TableExportRowSchema).max(20_000),
  /** صفُّ الإجمالي صريحًا؛ وإلّا جُمعت الأعمدة ذات `total`. `null` يُخفيه. */
  totals: z.record(z.string(), Cell).nullable().optional(),
  /** نصُّ خليّة «الإجمالي» — يوضع في أوّل عمودٍ ليس رقميًّا. */
  totalsLabel: z.string().trim().max(160).optional(),
});

export const TableExportSheetSchema = z.object({
  title: z.string().trim().min(1).max(160),
  /** المدة: `label` إن وُجد يُكتب كما هو، وإلّا «من … إلى …» من التاريخين (YYYY-MM-DD). */
  period: z
    .object({
      from: z.string().max(40).optional(),
      to: z.string().max(40).optional(),
      label: z.string().max(200).optional(),
    })
    .optional(),
  /** بياناتٌ في الرأس تحت المدة: «العميل: …»، «الهاتف: …» — لكشف الحساب وأمثاله. */
  details: z
    .array(z.object({ label: z.string().trim().min(1).max(60), value: z.string().max(300) }))
    .max(10)
    .optional(),
  sections: z.array(TableExportSectionSchema).min(1).max(20),
  /** اتجاهُ الصفحة في PDF؛ وإلّا اختير تلقائيًّا: عرضيّةٌ متى كثرت الأعمدة. */
  orientation: z.enum(['portrait', 'landscape']).optional(),
  /** سطورٌ تحت الجداول: حالةُ التوازن، الهامش… */
  notes: z.array(z.string().max(500)).max(20).optional(),
});

export const TableExportDocSchema = z.object({
  /** اسمُ الملفّ (بلا امتداد) وعنوانُ المعاينة. */
  title: z.string().trim().min(1).max(160),
  /** اسمُ الشركة في الرأس؛ إن غاب أُخذ من ملفّ الشركة. */
  company: z.string().trim().max(200).optional(),
  /** الورقةُ التي يُطبع عليها PDF إن رُفعت في الإعدادات — كشفُ الحساب ورقتُه، وغيرُه العامّة. */
  paper: z.enum(DOCUMENT_PAPER_KINDS).optional(),
  sheets: z.array(TableExportSheetSchema).min(1).max(10),
});

export type TableExportColumn = z.input<typeof TableExportColumnSchema>;
export type TableExportRow = z.input<typeof TableExportRowSchema>;
export type TableExportSection = z.input<typeof TableExportSectionSchema>;
export type TableExportSheet = z.input<typeof TableExportSheetSchema>;
export type TableExportDoc = z.input<typeof TableExportDocSchema>;
export type TableExportDocParsed = z.output<typeof TableExportDocSchema>;

/** العمودُ الذي تُزاح فيه صفوفُ الشجرة. */
export function tableExportIndentKey(section: TableExportSection): string | undefined {
  return (section.columns.find((c) => c.indent) ?? section.columns.find((c) => !c.kind || c.kind === 'text'))?.key;
}

/** «2026-11-01» ← «01/11/2026»: التاريخ رقميًّا يومًا/شهرًا/سنة كما يُعرض في النظام. */
export function tableExportDate(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
}

/** نصُّ المدة كما يُكتب في الرأس. */
export function tableExportPeriod(period: TableExportSheet['period'], fmt: (d: string) => string = tableExportDate): string | null {
  if (!period) return null;
  if (period.label) return period.label;
  if (period.from && period.to) return `من ${fmt(period.from)} إلى ${fmt(period.to)}`;
  if (period.to) return `حتى ${fmt(period.to)}`;
  if (period.from) return `من ${fmt(period.from)}`;
  return null;
}

/**
 * صفُّ الإجمالي لقسم: الصريحُ إن أُعطي، وإلّا مجموعُ الأعمدة ذات `total`
 * من الصفوف العاديّة والتجميعيّة لا الإجماليّة (فلا يُجمع إجماليٌّ مرّتين).
 * يعيد `null` حين لا إجماليّ.
 */
export function tableExportTotals(section: TableExportSection): Record<string, string | number | null | undefined> | null {
  if (section.totals === null) return null;
  const label = section.totalsLabel ?? 'الإجمالي';
  const labelKey = section.columns.find((c) => !c.kind || c.kind === 'text' || c.kind === 'date')?.key;

  if (section.totals) {
    const labelled = Object.values(section.totals).some((v) => typeof v === 'string');
    return labelKey && !labelled ? { [labelKey]: label, ...section.totals } : section.totals;
  }

  const summed = section.columns.filter((c) => c.total);
  if (summed.length === 0) return null;
  const out: Record<string, string | number | null | undefined> = {};
  if (labelKey) out[labelKey] = label;
  for (const c of summed) {
    let sum = 0;
    for (const r of section.rows) {
      if (r._style === 'total' || r._style === 'subtotal' || r._style === 'group' || r._style === 'note') continue;
      const v = r[c.key];
      if (typeof v === 'number') sum += v;
    }
    out[c.key] = Math.round(sum * 100) / 100;
  }
  return out;
}

/* ==========================================================================
   عرضُ الخلايا — الالتفاف والرموز والعروض
   ========================================================================== */

/**
 * رابطُ الخليّة إن كان لعمودها `hrefKey` وحمل الصفُّ عنوانًا صالحًا.
 * http/https وحدهما: رابطٌ من مستندٍ مُصدَّر لا يُنفَّذ («javascript:») ولا يفتح ملفًّا محلّيًّا.
 */
export function tableExportHref(
  col: TableExportColumn,
  row: Record<string, string | number | null | undefined>,
): string | null {
  if (!col.hrefKey) return null;
  const v = row[col.hrefKey];
  return typeof v === 'string' && /^https?:\/\/\S+$/i.test(v) ? v : null;
}

/** العمودُ يلتفّ أسطرًا؟ النصُّ نعم ما لم يُمنع، والتاريخُ والأرقامُ والرموزُ لا أبدًا. */
export function tableExportWraps(col: TableExportColumn): boolean {
  if (col.wrap !== undefined) return col.wrap;
  return !col.kind || col.kind === 'text';
}

/**
 * رقمُ مستندٍ أو قيدٍ داخل نصّ: «INV-2026-0120»، «JV-2026-3169»، «PAY-2026-0137».
 * حروفٌ لاتينيّةٌ كبيرة ثمّ مقاطعُ بشَرطةٍ آخرُها رقم. يُعزل في العرض اتجاهًا
 * لاتينيًّا ولا يُكسر عند شَرطته — وإلّا قرأته العربيّةُ حوله «-INV-2026 0120».
 */
export const TABLE_EXPORT_CODE_RE = /\b[A-Z]{2,6}(?:-[A-Z0-9]+)*-\d+\b/g;

/** النصُّ مقاطعَ: ما هو رمزٌ وما ليس رمزًا، بترتيبه — لعزل الرموز في العرض. */
export function tableExportSplitCodes(text: string): Array<{ text: string; code: boolean }> {
  const out: Array<{ text: string; code: boolean }> = [];
  let at = 0;
  for (const m of text.matchAll(TABLE_EXPORT_CODE_RE)) {
    const i = m.index ?? 0;
    if (i > at) out.push({ text: text.slice(at, i), code: false });
    out.push({ text: m[0], code: true });
    at = i + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at), code: false });
  return out;
}

/**
 * طولُ الخلية بالحروف كما ستُعرض — تقديرٌ لعرض العمود.
 * المالُ بكسرين وفواصل الآلاف وقوسَي السالب، والتاريخُ عشرةُ أحرف.
 */
function cellLength(col: TableExportColumn, value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  if (col.kind === 'date') return 10;
  if (typeof value === 'number') {
    const int = Math.trunc(Math.abs(value)).toString().length;
    const grouped = int + Math.floor((int - 1) / 3);
    if (col.kind === 'money') return grouped + 3 + (value < 0 ? 2 : 0);
    return grouped + (Number.isInteger(value) ? 0 : 3) + (value < 0 ? 1 : 0) + (col.kind === 'percent' ? 1 : 0);
  }
  /* النصُّ متعدّدُ الأسطر: أطولُ سطوره. */
  return Math.max(...value.split('\n').map((l) => l.length));
}

/**
 * عرضُ أعمدة القسم بالحروف من محتواه — لإكسل.
 *
 * الأعمدةُ التي لا تلتفّ (تاريخٌ ورقمٌ ومبلغٌ ورمز) بعرض أطول قيمةٍ فيها
 * فلا تُقصّ ولا تُكسر، والنصُّ بعرض محتواه حتى سقفٍ يلتفّ بعده. والعرضُ
 * الصريح (`width`) يغلب، و`minWidth` حدٌّ أدنى. يُقاس من أوّل ألفي صفّ —
 * تكفي لتقدير العرض ولا تُثقل التصدير الكبير.
 */
export function tableExportColumnWidths(section: TableExportSection, textCap = 50): number[] {
  const totals = tableExportTotals(section);
  const sample = section.rows.slice(0, 2000);
  return section.columns.map((c) => {
    if (c.width) return Math.max(c.width, c.minWidth ?? 0);
    const wraps = tableExportWraps(c);
    /* العنوانُ يلتفّ في الرأس عند الحاجة: أطولُ كلمةٍ فيه حدٌّ أدنى لا طولُه كلّه. */
    const label = Math.max(...c.label.split(/\s+/).map((w) => w.length));
    let content = 0;
    for (const r of sample) content = Math.max(content, cellLength(c, r[c.key]));
    /* نصُّ «الإجمالي» يلتفّ في عموده النصّيّ، فلا يوسّعه وحده. */
    if (totals && !wraps) content = Math.max(content, cellLength(c, totals[c.key]));
    const fitted = Math.ceil(Math.max(label, content) + 2);
    return Math.max(Math.min(fitted, wraps ? textCap : 60), c.minWidth ?? 0, 8);
  });
}
